<script setup>
/**
 * This component handles parsing and sending WhatsApp message templates.
 * It works as follows:
 * 1. Displays the template text with variable placeholders.
 * 2. Generates input fields for each variable in the template.
 * 3. Validates that all variables are filled before sending.
 * 4. Replaces placeholders with user-provided values.
 * 5. Emits events to send the processed message or reset the template.
 *
 * AUTHENTICATION templates (Meta OTP rules):
 * - Preset body text; agent provides a single OTP (max 15 chars).
 * - The same OTP value must be sent in the body parameter AND in every URL
 *   button parameter (Meta converts OTP buttons to type URL upon approval).
 * - No custom header, media, or extra body variables.
 */
import { ref, computed, onMounted, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import FileInput from 'dashboard/components/widgets/conversation/WhatsappTemplates/FileInput.vue';
import {
  buildTemplateParameters,
  allKeysRequired,
  replaceTemplateVariables,
  DEFAULT_LANGUAGE,
  DEFAULT_CATEGORY,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
} from 'dashboard/helper/templateHelper';

const props = defineProps({
  template: {
    type: Object,
    default: () => ({}),
    validator: value => {
      if (!value || typeof value !== 'object') return false;
      if (!value.components || !Array.isArray(value.components)) return false;
      return true;
    },
  },
});

const emit = defineEmits(['sendMessage', 'resetTemplate', 'back']);

const OTP_MAX_LENGTH = 15;

const { t } = useI18n();

const processedParams = ref(buildTemplateParameters(props.template));
const mediaInputKey = ref(0);

const isAuthentication = computed(
  () => props.template?.category === 'AUTHENTICATION'
);

const languageLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.LANGUAGE')}: ${props.template.language || DEFAULT_LANGUAGE}`;
});

const categoryLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.CATEGORY')}: ${props.template.category || DEFAULT_CATEGORY}`;
});

const headerComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.HEADER);
});

const bodyComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.BODY);
});

const bodyText = computed(() => {
  return bodyComponent.value?.text || '';
});

const hasMediaHeader = computed(() =>
  MEDIA_FORMATS.includes(headerComponent.value?.format?.toUpperCase())
);

const hasLocationHeader = computed(
  () => headerComponent.value?.format?.toUpperCase() === 'LOCATION'
);

const hasHeaderInputs = computed(
  () => hasMediaHeader.value || hasLocationHeader.value
);

const formatType = computed(() => {
  const format = headerComponent.value?.format;
  return format ? format.charAt(0) + format.slice(1).toLowerCase() : '';
});

const acceptedTypes = {
  image: 'image/*',
  video: 'video/*',
  document: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt',
};

const acceptedFileType = computed(() => {
  const mediaType = headerComponent.value?.format?.toLowerCase();
  return acceptedTypes[mediaType] || '';
});

const isDocumentTemplate = computed(() => {
  return headerComponent.value?.format?.toLowerCase() === 'document';
});

const hasVariables = computed(() => {
  if (isAuthentication.value) return false;
  return bodyText.value?.match(/{{([^}]+)}}/g) !== null;
});

const renderedTemplate = computed(() => {
  if (isAuthentication.value) {
    const otp = processedParams.value.auth?.otp || '';
    return bodyText.value.replace(/{{([^}]+)}}/g, otp || '$&');
  }
  return replaceTemplateVariables(bodyText.value, processedParams.value);
});

// Collect the indices of URL buttons in the approved template so we can mirror the OTP.
const authUrlButtonIndices = computed(() => {
  if (!isAuthentication.value) return [];
  const indices = [];
  props.template.components?.forEach(comp => {
    if (comp.type !== COMPONENT_TYPES.BUTTONS) return;
    comp.buttons?.forEach((btn, idx) => {
      if (btn.type === 'URL') indices.push(idx);
    });
  });
  return indices;
});

const otpValue = computed(() => processedParams.value.auth?.otp || '');

const otpError = computed(() => {
  if (!isAuthentication.value) return '';
  const otp = otpValue.value;
  if (!otp) return t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE');
  if (otp.length > OTP_MAX_LENGTH)
    return t('WHATSAPP_TEMPLATES.PARSER.OTP_MAX_LENGTH_ERROR');
  return '';
});

const isFormInvalid = computed(() => {
  if (isAuthentication.value) return !!otpError.value;

  if (!hasVariables.value && !hasHeaderInputs.value) return false;

  if (hasLocationHeader.value) {
    const h = processedParams.value.header;
    if (!h?.latitude?.toString().trim() || !h?.longitude?.toString().trim()) {
      return true;
    }
  }

  if (hasMediaHeader.value && !processedParams.value.header?.media_url) {
    return true;
  }

  if (hasVariables.value && processedParams.value.body) {
    const hasEmptyBodyVariable = Object.values(processedParams.value.body).some(
      value => !value
    );
    if (hasEmptyBodyVariable) return true;
  }

  if (processedParams.value.buttons) {
    const hasEmptyButtonParameter = processedParams.value.buttons.some(
      button => !button.parameter
    );
    if (hasEmptyButtonParameter) return true;
  }

  return false;
});

const v$ = useVuelidate(
  {
    processedParams: {
      requiredIfKeysPresent: requiredIf(hasVariables),
      allKeysRequired,
    },
  },
  { processedParams }
);

const initializeTemplateParameters = () => {
  processedParams.value = buildTemplateParameters(props.template);
};

const updateMediaUrl = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_url = value;
};

const onFileName = fileName => {
  if (!isDocumentTemplate.value || !fileName) return;
  processedParams.value.header ??= {};
  processedParams.value.header.media_name = fileName;
};

const updateMediaName = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_name = value;
};

const hasUploadedMedia = computed(
  () => !!processedParams.value.header?.media_url
);

const removeMediaAttachment = () => {
  if (!processedParams.value.header) return;
  processedParams.value.header.media_url = '';
  processedParams.value.header.media_name = '';
  mediaInputKey.value += 1;
};

/**
 * Build the processed_params payload for an AUTHENTICATION template.
 * Meta requires the OTP in both the body parameter and each URL button parameter.
 */
const buildAuthProcessedParams = otp => {
  const params = {
    body: { 1: otp },
  };
  if (authUrlButtonIndices.value.length > 0) {
    params.buttons = [];
    authUrlButtonIndices.value.forEach(idx => {
      params.buttons[idx] = { type: 'url', parameter: otp };
    });
  }
  return params;
};

const sendMessage = () => {
  if (isAuthentication.value) {
    if (otpError.value) return;
    const { name, category, language, namespace } = props.template;
    const otp = otpValue.value;
    emit('sendMessage', {
      message: renderedTemplate.value,
      templateParams: {
        name,
        category,
        language,
        namespace,
        processed_params: buildAuthProcessedParams(otp),
      },
    });
    return;
  }

  v$.value.$touch();
  if (v$.value.$invalid) return;

  const { name, category, language, namespace } = props.template;

  const payload = {
    message: renderedTemplate.value,
    templateParams: {
      name,
      category,
      language,
      namespace,
      processed_params: processedParams.value,
    },
  };
  emit('sendMessage', payload);
};

const resetTemplate = () => {
  emit('resetTemplate');
};

const goBack = () => {
  emit('back');
};

onMounted(initializeTemplateParameters);

watch(
  () => props.template,
  () => {
    initializeTemplateParameters();
    v$.value.$reset();
  },
  { deep: true }
);

defineExpose({
  processedParams,
  hasVariables,
  hasMediaHeader,
  hasLocationHeader,
  hasHeaderInputs,
  isDocumentTemplate,
  headerComponent,
  renderedTemplate,
  v$,
  updateMediaUrl,
  updateMediaName,
  sendMessage,
  resetTemplate,
  goBack,
});
</script>

<template>
  <div>
    <div class="flex flex-col gap-4 p-4 mb-4 rounded-lg bg-n-alpha-black2">
      <div class="flex justify-between items-center">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ template.name }}
        </h3>
        <span class="text-xs text-n-slate-11">
          {{ languageLabel }}
        </span>
      </div>

      <div class="flex flex-col gap-2">
        <div class="rounded-md">
          <div class="text-sm whitespace-pre-wrap text-n-slate-12">
            {{ renderedTemplate }}
          </div>
        </div>
      </div>

      <div class="text-xs text-n-slate-11">
        {{ categoryLabel }}
      </div>
    </div>

    <!-- Authentication OTP section -->
    <div v-if="isAuthentication && processedParams.auth" class="mb-4">
      <p class="mb-2 text-sm font-semibold">
        {{ t('WHATSAPP_TEMPLATES.PARSER.OTP_LABEL') }}
      </p>
      <Input
        v-model="processedParams.auth.otp"
        type="text"
        class="w-full"
        :placeholder="t('WHATSAPP_TEMPLATES.PARSER.OTP_CODE')"
        :maxlength="OTP_MAX_LENGTH"
      />
      <p v-if="otpError" class="mt-1 text-xs text-n-ruby-9">
        {{ otpError }}
      </p>
    </div>

    <div v-if="!isAuthentication && (hasVariables || hasHeaderInputs)">
      <div v-if="hasMediaHeader" class="mb-4">
        <p class="mb-2.5 text-sm font-semibold">
          {{
            $t('WHATSAPP_TEMPLATES.PARSER.MEDIA_HEADER_LABEL', {
              type: formatType,
            }) || `${formatType} Header`
          }}
        </p>
        <div class="flex items-center mb-2.5">
          <FileInput
            :key="mediaInputKey"
            class="flex-1"
            :accept="acceptedFileType"
            :initial-file-name="processedParams.header?.media_name || ''"
            @input="updateMediaUrl"
            @file-name="onFileName"
          />
        </div>
        <div v-if="hasUploadedMedia" class="mb-2.5 flex justify-end">
          <woot-button
            variant="clear"
            size="small"
            @click="removeMediaAttachment"
          >
            {{ t('CAMPAIGN.MEDIA.REMOVE_FILE') }}
          </woot-button>
        </div>
        <div v-if="isDocumentTemplate" class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_name || ''"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.DOCUMENT_NAME_PLACEHOLDER')
            "
            @update:model-value="updateMediaName"
          />
        </div>
      </div>

      <div v-if="hasLocationHeader" class="mb-4">
        <p class="mb-2.5 text-sm font-semibold">
          {{ t('WHATSAPP_TEMPLATES.PARSER.LOCATION_HEADER_LABEL') }}
        </p>
        <div class="flex flex-col gap-2.5">
          <Input
            v-model="processedParams.header.latitude"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.LOCATION_LATITUDE_PLACEHOLDER')
            "
          />
          <Input
            v-model="processedParams.header.longitude"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.LOCATION_LONGITUDE_PLACEHOLDER')
            "
          />
          <Input
            v-model="processedParams.header.name"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.LOCATION_NAME_PLACEHOLDER')
            "
          />
          <Input
            v-model="processedParams.header.address"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.LOCATION_ADDRESS_PLACEHOLDER')
            "
          />
        </div>
      </div>

      <!-- Body Variables Section -->
      <div v-if="processedParams.body">
        <p class="mb-2.5 text-sm font-semibold">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
        </p>
        <div
          v-for="(variable, key) in processedParams.body"
          :key="`body-${key}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.body[key]"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                variable: key,
              })
            "
          />
        </div>
      </div>

      <!-- Button Variables Section -->
      <div v-if="processedParams.buttons">
        <p class="mb-2.5 text-sm font-semibold">
          {{ t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETERS') }}
        </p>
        <div
          v-for="(button, index) in processedParams.buttons"
          :key="`button-${index}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.buttons[index].parameter"
            type="text"
            class="flex-1"
            :placeholder="t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETER')"
          />
        </div>
      </div>
      <p
        v-if="v$.$dirty && v$.$invalid"
        class="p-2.5 text-center rounded-md bg-n-ruby-9/20 text-n-ruby-9"
      >
        {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>
    </div>

    <slot
      name="actions"
      :send-message="sendMessage"
      :reset-template="resetTemplate"
      :go-back="goBack"
      :is-valid="!v$.$invalid"
      :disabled="isFormInvalid"
    />
  </div>
</template>
